import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export function computeBudgetFromOffer(offerData) {
  if (!offerData?.budgets?.length) return { hours: 0, services: [] };
  const budget = offerData.budgets[offerData.budgets.length - 1];
  const resources = budget.resources || [];
  const activities = budget.activities || [];

  const months = [];
  if (budget.startMonth && budget.endMonth) {
    let [y, m] = budget.startMonth.split('-').map(Number);
    const [ey, em] = budget.endMonth.split('-').map(Number);
    while ((y < ey || (y === ey && m <= em)) && months.length < 36) {
      months.push(`${y}-${String(m).padStart(2, '0')}`);
      m++; if (m > 12) { m = 1; y++; }
    }
  }

  const resHours = {};
  activities.forEach(act => {
    const hrs = months.reduce((s, mo) => s + (parseFloat(act.months?.[mo]) || 0), 0);
    resHours[act.resourceId] = (resHours[act.resourceId] || 0) + hrs;
  });

  const totalHours = Object.values(resHours).reduce((s, h) => s + h, 0);

  const seen = new Set();
  const services = resources
    .filter(r => r.hourlyRate && !seen.has(r.name) && seen.add(r.name))
    .map(r => ({ name: r.name || 'Konsult', hourlyRate: parseFloat(r.hourlyRate) || 0 }));

  return { hours: Math.round(totalHours), services };
}

export async function buildOrderConfirmationPdf(data) {
  const logoUrl = data._logoUrl;
  let logoBase64 = null;
  let logoMime = 'image/png';
  if (logoUrl) {
    try {
      const res = await fetch(logoUrl);
      const blob = await res.blob();
      logoMime = blob.type || 'image/png';
      logoBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (_) {}
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentW = pageW - 2 * margin;
  const ORANGE = [255, 107, 0];
  const DARK = [26, 26, 26];
  const GRAY = [100, 100, 100];
  const fmt = logoMime.includes('jpeg') || logoMime.includes('jpg') ? 'JPEG' : 'PNG';

  async function drawLogo(x, y, maxW, maxH) {
    if (!logoBase64) return;
    try {
      const dims = await new Promise(res => {
        const img = new window.Image();
        img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => res(null);
        img.src = logoBase64;
      });
      if (!dims) return;
      const ratio = dims.w / dims.h;
      let drawW = maxW, drawH = maxW / ratio;
      if (drawH > maxH) { drawH = maxH; drawW = maxH * ratio; }
      pdf.addImage(logoBase64, fmt, x, y, drawW, drawH, undefined, 'FAST');
    } catch (_) {}
  }

  function drawPageHeader(pageTitle) {
    const headerH = 24;
    pdf.setFillColor(...DARK);
    pdf.rect(0, 0, pageW, headerH, 'F');
    drawLogo(margin, (headerH - 14) / 2, 40, 14);
    pdf.setFontSize(15); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
    pdf.text('ORDERBEKRÄFTELSE', pageW - margin, 11, { align: 'right' });
    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(200, 200, 200);
    pdf.text(`DATUM ${data.signingDate || new Date().toISOString().slice(0, 10)}`, pageW - margin, 18, { align: 'right' });
    if (pageTitle) {
      pdf.setFontSize(8); pdf.setTextColor(180, 180, 180);
      pdf.text(pageTitle, margin + 44, 14);
    }
    return headerH;
  }

  // ══════════════ PAGE 1 ══════════════
  const headerH = drawPageHeader('');

  // Left sidebar meta
  const sideW = 50;
  const mainX = margin + sideW + 6;
  let sideY = headerH + 10;

  function metaItem(label, val) {
    if (!val) return;
    pdf.setFontSize(6); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...ORANGE);
    pdf.text(label, margin, sideY);
    sideY += 3.5;
    pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK);
    const lines = pdf.splitTextToSize(String(val), sideW - 2);
    pdf.text(lines, margin, sideY);
    sideY += lines.length * 3.8 + 3;
  }

  metaItem('OFFERTNUMMER', data.offerNumber || data.confirmationNumber);
  metaItem('VÅR PART', data.supplierContactName);
  metaItem('ER PART', data.clientContactName);
  metaItem('E-POST', data.clientContactEmail);
  metaItem('TELEFON', data.clientContactPhone);

  // Right side: title heading
  pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK);
  pdf.text('ORDERBEKRÄFTELSE', mainX, headerH + 16);
  pdf.setFontSize(8); pdf.setFont('helvetica', 'italic'); pdf.setTextColor(...GRAY);
  pdf.text('För generella villkor se sista sidan.', mainX, headerH + 23);

  const postMetaY = Math.max(sideY + 4, headerH + 32);
  pdf.setDrawColor(210, 210, 210); pdf.setLineWidth(0.2);
  pdf.line(margin, postMetaY, pageW - margin, postMetaY);
  let y = postMetaY + 6;

  // ── PARTER ────────────────────────────────────────────────────────────────
  const clientAddr = [data.clientAddress, data.clientZip, data.clientCity].filter(Boolean).join(' ') || data.clientAddress || '';
  const supplierAddr = data.supplierAddress || '';

  function fmtContact(phone, mobile) {
    const parts = [];
    if (phone) parts.push(`Tel: ${phone}`);
    if (mobile) parts.push(`Mob: ${mobile}`);
    return parts.join('\n');
  }

  autoTable(pdf, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentW,
    head: [[
      { content: 'Leverantör', styles: { fontStyle: 'bold', fillColor: DARK, textColor: [255, 255, 255] } },
      { content: 'Beställare/Fakturamottagare', styles: { fontStyle: 'bold', fillColor: DARK, textColor: [255, 255, 255] } },
    ]],
    body: [
      [data.supplierName || '', { content: data.clientName || '', styles: { fontStyle: 'bold' } }],
      [`Org.nr: ${data.supplierOrgNumber || '–'}`, `Org.nr: ${data.clientOrgNumber || '–'}`],
      [supplierAddr, clientAddr],
      [`Kontakt: ${data.supplierContactName || '–'}`, `Kontakt: ${data.clientContactName || '–'}`],
      [fmtContact(data.supplierPhone, data.supplierContactPhone), fmtContact(data.clientPhone, data.clientContactPhone)],
      [data.supplierContactEmail || '', data.clientContactEmail || ''],
    ],
    styles: { fontSize: 8, cellPadding: { top: 2, right: 4, bottom: 2, left: 4 } },
    columnStyles: { 0: { textColor: DARK }, 1: { textColor: DARK } },
    theme: 'grid',
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.2,
  });
  y = pdf.lastAutoTable.finalY + 5;

  // ── UPPDRAGSINFORMATION ───────────────────────────────────────────────────
  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK);
  pdf.text('Uppdragsinformation', margin, y);
  y += 1.5;
  pdf.setDrawColor(...ORANGE); pdf.setLineWidth(0.5);
  pdf.line(margin, y, pageW - margin, y);
  y += 2;

  const projRows = [
    ['Projektnamn', data.ourProjectName || data.projectName || ''],
    data.clientProjectName ? ['Projektnamn (kund)', data.clientProjectName] : null,
    data.offerNumber ? ['Offertnummer', data.offerNumber] : null,
    (data.projectNumber || data.clientProjectNumber) ? ['Projektnummer', data.projectNumber || data.clientProjectNumber] : null,
    data.projectAddress ? ['Projektadress', data.projectAddress] : null,
    ['Fakturamärkning/referens', data.invoiceReference || ''],
    data.timeline ? ['Tidplan', data.timeline] : null,
    data.clientInvoiceEmail ? ['Faktura e-post', data.clientInvoiceEmail] : null,
  ].filter(Boolean);

  autoTable(pdf, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentW,
    head: [],
    body: projRows,
    styles: { fontSize: 8, cellPadding: { top: 2, right: 4, bottom: 2, left: 4 } },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 58, textColor: DARK },
      1: { textColor: DARK },
    },
    theme: 'grid',
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.2,
  });

  // ══════════════ PAGE 2 ══════════════
  pdf.addPage();
  const h2 = drawPageHeader(`${data.confirmationNumber || ''} – ${data.ourProjectName || data.projectName || ''}`);
  y = h2 + 8;

  function sectionHeading(label) {
    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK);
    pdf.text(label, margin, y);
    y += 1.5;
    pdf.setDrawColor(...ORANGE); pdf.setLineWidth(0.5);
    pdf.line(margin, y, pageW - margin, y);
    y += 5;
  }

  // ── UPPDRAGSBESKRIVNING ───────────────────────────────────────────────────
  if (data.taskDescription) {
    sectionHeading('Uppdragsbeskrivning');
    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK);
    const lines = data.taskDescription.split('\n').filter(l => l.trim());
    lines.forEach(line => {
      const isBullet = /^[-•*]/.test(line.trim());
      const text = isBullet ? '• ' + line.trim().replace(/^[-•*]\s*/, '') : line.trim();
      const indent = isBullet ? 4 : 0;
      const wrapped = pdf.splitTextToSize(text, contentW - indent - 2);
      if (y + wrapped.length * 4.5 > pageH - 20) { pdf.addPage(); y = 20; }
      pdf.text(wrapped, margin + indent, y);
      y += wrapped.length * 4.5 + 1;
    });
    y += 4;
  }

  // ── BUDGET ────────────────────────────────────────────────────────────────
  if (data.budgetHours) {
    sectionHeading('Budget');
    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK);
    pdf.text(`Uppskattad budget: ${data.budgetHours}h.`, margin, y);
    y += 8;
  }

  // ── ARVODE ────────────────────────────────────────────────────────────────
  sectionHeading('Arvode');
  pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...DARK);
  const arvodesText = data.arvodesText || 'Ersättning enligt löpande räkning mot budget. Samtliga priser exklusive moms.';
  const arvodesWrapped = pdf.splitTextToSize(arvodesText, contentW);
  pdf.text(arvodesWrapped, margin, y);
  y += arvodesWrapped.length * 4.5 + 5;

  // ── SPECIFIKATION ─────────────────────────────────────────────────────────
  if (data.services?.length) {
    autoTable(pdf, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: contentW,
      head: [['Specifikation / Tjänst', 'Timpris SEK/h']],
      body: data.services.map(s => [
        s.name,
        Number(s.hourlyRate).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      ]),
      styles: { fontSize: 8, cellPadding: { top: 2, right: 4, bottom: 2, left: 4 } },
      headStyles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { textColor: DARK },
        1: { textColor: DARK, halign: 'right', cellWidth: 40 },
      },
      theme: 'grid',
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.2,
    });
    y = pdf.lastAutoTable.finalY + 6;
  }

  // ── VILLKOR ───────────────────────────────────────────────────────────────
  const termsRows = [
    ['Fakturering', `Betalningsvillkor: ${data.paymentTerms || '30 dagar netto'}`],
    ['Försäkring', data.insurance || 'Konsulten har en giltig ansvarsförsäkring'],
    ['Generella villkor', `Uppdragets omfattning bestäms av kontraktsförhandlingarna med följande ordningsföljd:\n1. Detta avtal\n2. ${data.standardTerms || 'ABK 09'} (bifogas inte)`],
  ];

  autoTable(pdf, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentW,
    head: [],
    body: termsRows,
    styles: { fontSize: 8, cellPadding: { top: 2, right: 4, bottom: 2, left: 4 } },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40, textColor: DARK },
      1: { textColor: DARK },
    },
    theme: 'grid',
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.2,
  });
  y = pdf.lastAutoTable.finalY + 8;

  // ── SIGNATUR ──────────────────────────────────────────────────────────────
  autoTable(pdf, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentW,
    head: [[
      { content: 'Leverantörs underskrift', styles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: 'bold' } },
      { content: 'Beställares underskrift', styles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: 'bold' } },
    ]],
    body: [
      [`Ort, datum`, 'Ort, datum'],
      [`${data.signingCity || 'Solna'}, ${data.signingDate || ''}`, ''],
      [data.supplierName || '', data.clientName || ''],
      ['Signatur', 'Signatur'],
      [' ', ' '],
      [`Namn: ${data.supplierContactName || ''}`, 'Namn:'],
    ],
    styles: { fontSize: 8, cellPadding: { top: 2, right: 4, bottom: 2, left: 4 } },
    columnStyles: {
      0: { textColor: DARK },
      1: { textColor: DARK },
    },
    didParseCell: (hookData) => {
      if (hookData.row.index === 4) {
        hookData.cell.styles.minCellHeight = 16;
      }
      if (hookData.row.index === 1 || hookData.row.index === 2) {
        if (hookData.row.index === 1) hookData.cell.styles.fontStyle = 'bold';
      }
    },
    theme: 'grid',
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.2,
  });
  y = pdf.lastAutoTable.finalY + 8;

  // ── FOOTER ────────────────────────────────────────────────────────────────
  const footerY = pageH - 14;
  pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.2);
  pdf.line(margin, footerY - 4, pageW - margin, footerY - 4);
  pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GRAY);
  const footerParts = [
    data.supplierName,
    data.supplierAddress,
    data.supplierPhone,
    data.supplierEmail,
    data.supplierOrgNumber ? `Org.nr: ${data.supplierOrgNumber}` : null,
  ].filter(Boolean);
  pdf.text(footerParts.join(' | '), pageW / 2, footerY, { align: 'center' });

  const safeName = `${data.confirmationNumber || 'orderbekraftelse'}_${(data.clientName || 'kund').replace(/\s+/g, '_')}.pdf`;
  pdf.save(safeName);
}
